/**
 * IgGridTab — the @flaxitup Instagram feed rendered as a 3-column square
 * grid, IG-style. Newest first. Hover reveals engagement badges; click a
 * tile opens a caption + stats panel on the right.
 *
 * Data source is marketing.meta_posts (populated by meta-sync). If the sync
 * has drifted, the header shows how old the newest post is + links out.
 */
import { useState, useMemo } from 'react';
import { Heart, MessageCircle, Film, Layers, ImageIcon, ExternalLink, Play, Zap } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/charts/EmptyState';
import { cn } from '@/lib/utils';
import { imgProxy } from '@/lib/img';
import { useIgGrid, type IgPost } from '@/hooks/useIgGrid';

export function IgGridTab() {
  const q = useIgGrid(36);
  const [openId, setOpenId] = useState<string | null>(null);

  const posts = useMemo(() => q.data ?? [], [q.data]);
  const active = useMemo(() => posts.find((p) => p.media_id === openId) ?? null, [posts, openId]);
  const stale = useMemo(() => {
    const first = posts[0];
    if (!first) return null;
    const days = Math.floor((Date.now() - new Date(first.published_at).getTime()) / 86400_000);
    return days > 2 ? days : null;
  }, [posts]);

  if (q.isLoading) {
    return (
      <div className="grid grid-cols-3 gap-0.5">
        {Array.from({ length: 12 }).map((_, i) => <Skeleton key={i} className="aspect-square w-full" />)}
      </div>
    );
  }
  if (q.isError) return <EmptyState title="Couldn't load feed" description={String(q.error)} />;
  if (posts.length === 0) return <EmptyState title="No posts synced yet" description="Trigger meta-sync to pull @flaxitup's feed." />;

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div>
            @flaxitup · {posts.length} posts
            {stale && (
              <span className="ml-2 rounded bg-amber-500/10 px-1.5 py-0.5 text-2xs text-amber-600 dark:text-amber-400">
                latest {stale} days old — meta-sync may need a run
              </span>
            )}
          </div>
        </div>
        <div className="mx-auto grid w-full max-w-[480px] grid-cols-3 gap-0.5 lg:mx-0">
          {posts.map((p) => (
            <IgTile key={p.media_id} post={p} active={openId === p.media_id} onClick={() => setOpenId(openId === p.media_id ? null : p.media_id)} />
          ))}
        </div>
      </div>

      <PostDetail post={active} />
    </div>
  );
}

function IgTile({ post, active, onClick }: { post: IgPost; active: boolean; onClick: () => void }) {
  const src = imgProxy(post.thumbnail_url ?? post.image_url, post.r2_thumb_key);
  const isVideo = post.media_type === 'VIDEO' || post.media_type === 'REEL';
  const isCarousel = post.media_type === 'CAROUSEL_ALBUM';
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group relative aspect-square overflow-hidden bg-muted/30 transition-all',
        active && 'ring-2 ring-violet-500',
      )}
    >
      {src ? (
        <img src={src} alt="" loading="lazy" className="h-full w-full bg-black object-contain" />
      ) : (
        <div className="flex h-full items-center justify-center text-muted-foreground/60">
          <ImageIcon className="h-5 w-5" />
        </div>
      )}
      <div className="absolute right-1 top-1 text-white/90 drop-shadow">
        {isCarousel && <Layers className="h-3.5 w-3.5" />}
        {isVideo && <Film className="h-3.5 w-3.5" />}
      </div>
      {post.is_boosted && (
        <div className="absolute left-1 top-1 flex items-center gap-0.5 rounded bg-emerald-500/90 px-1 py-0.5 text-2xs text-white">
          <Zap className="h-2.5 w-2.5" /> boost
        </div>
      )}
      {isVideo && post.views != null && post.views > 0 && (
        <div className="pointer-events-none absolute bottom-1 left-1 flex items-center gap-0.5 rounded bg-black/60 px-1 py-0.5 text-2xs text-white tabular-nums backdrop-blur">
          <Play className="h-2.5 w-2.5 fill-current" /> {formatCompact(post.views)}
        </div>
      )}
      <div className="pointer-events-none absolute inset-0 flex items-end justify-center gap-3 bg-gradient-to-t from-black/70 to-transparent p-1.5 text-2xs text-white opacity-0 transition-opacity group-hover:opacity-100">
        <span className="flex items-center gap-0.5 tabular-nums"><Heart className="h-3 w-3" /> {post.like_count ?? 0}</span>
        <span className="flex items-center gap-0.5 tabular-nums"><MessageCircle className="h-3 w-3" /> {post.comments_count ?? 0}</span>
        {isVideo && post.views != null && post.views > 0 && (
          <span className="flex items-center gap-0.5 tabular-nums"><Play className="h-3 w-3" /> {formatCompact(post.views)}</span>
        )}
      </div>
    </button>
  );
}

function formatCompact(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(n);
}

function PostDetail({ post }: { post: IgPost | null }) {
  if (!post) {
    return (
      <div className="rounded-md border border-dashed border-border/50 bg-muted/20 p-4 text-center text-xs text-muted-foreground">
        Click a tile to see caption + stats
      </div>
    );
  }
  const src = imgProxy(post.thumbnail_url ?? post.image_url, post.r2_thumb_key);
  const isVideo = post.media_type === 'VIDEO' || post.media_type === 'REEL';
  return (
    <div className="space-y-3 rounded-md border border-border/50 p-3">
      {src && <img src={src} alt="" className="aspect-square w-full rounded bg-black object-contain" />}
      <div className="flex flex-wrap items-center gap-2 text-2xs text-muted-foreground">
        <span className="rounded bg-muted/50 px-1.5 py-0.5 uppercase">{post.media_type ?? 'IMAGE'}</span>
        <span>{new Date(post.published_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
        {post.is_boosted && <span className="rounded bg-emerald-500/10 px-1.5 py-0.5 text-emerald-600 dark:text-emerald-400">boosted</span>}
      </div>
      <div className="grid grid-cols-3 gap-2 text-center text-xs">
        <Stat label="Likes" value={post.like_count} />
        <Stat label="Comments" value={post.comments_count} />
        <Stat label={isVideo ? 'Views' : 'Reach'} value={isVideo ? post.views : post.reach} />
      </div>
      {post.caption && (
        <p className="max-h-40 overflow-y-auto whitespace-pre-wrap text-sm leading-snug text-foreground/90">
          {post.caption}
        </p>
      )}
      {post.permalink && (
        <a
          href={post.permalink}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-violet-500 hover:underline"
        >
          <ExternalLink className="h-3 w-3" /> Open on Instagram
        </a>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | null }) {
  return (
    <div className="rounded-md border border-border/40 bg-muted/20 py-1.5">
      <div className="text-lg font-semibold tabular-nums">{value != null ? value.toLocaleString('en-IN') : '—'}</div>
      <div className="text-2xs uppercase text-muted-foreground">{label}</div>
    </div>
  );
}
